import { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";

interface CrumbItem {
  label: string;
  href?: string;
}

const pathLabels: Record<string, string> = {
  about: "關於我們",
  articles: "文章列表",
  topics: "探索主題",
  locations: "探索城市",
  stores: "探店精選",
  authors: "作者專區",
  tags: "標籤總覽",
  search: "搜尋",
  privacy: "隱私政策",
  terms: "使用條款",
  partners: "合作夥伴",
  "join-us": "加入我們",
  faq: "常見問題",
  events: "近期活動",
  "danshui-market-2026": "2026淡江春日市集",
};

function formatSlugLabel(slug: string): string {
  return decodeURIComponent(slug)
    .replace(/-/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export default function Breadcrumb() {
  const location = useLocation();

  const crumbs = useMemo<CrumbItem[]>(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return [];

    const result: CrumbItem[] = [{ label: "首頁", href: "/" }];

    let builtPath = "";
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      builtPath += `/${seg}`;

      const label = pathLabels[seg] || formatSlugLabel(seg);
      const isLast = i === segments.length - 1;

      if (isLast) {
        result.push({ label });
      } else {
        result.push({ label, href: builtPath });
      }
    }

    return result;
  }, [location.pathname]);

  if (crumbs.length <= 1) return null;

  return (
    <nav className="w-full px-4 md:px-8 lg:px-12 py-2.5 bg-slate-50 border-b border-slate-100 mt-16" aria-label="breadcrumb">
      <ol className="max-w-6xl mx-auto flex items-center flex-wrap gap-1 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={index} className="flex items-center gap-1">
              {index > 0 && (
                <span className="text-slate-400 mx-1">
                  <i className="ri-arrow-right-s-line" />
                </span>
              )}
              {isLast || !crumb.href ? (
                <span className="text-slate-500 font-medium">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.href}
                  className="text-slate-600 hover:text-primary transition-colors font-medium"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}